import { observable, action, computed } from 'mobx';
import { api } from '../services';
import { leadsStore } from './index';

class CommunicationsStore {
  @observable
  visiblePopover = false;

  @observable
  loading = false;

  @observable
  lastUnreadCount = 0;

  @observable
  lastList = [];

  @observable
  unreadThreads = [];

  @observable
  unreadMessages = [];

  @observable
  remoteNotified = {};

  constructor(leadsStore) {
    this.leadsStore = leadsStore;
  }

  @action
  load() {
    this.loading = true;
    Promise.all([api('getCommunicationsIncoming'), api('getSystemCommunications')]).then(([responseCommunications, responseNotifications]) => {
      let unreadCount = { communications: 0, notifications: 0 };
      let remoteNotified = { communications: null, notifications: null };
      let toBeNotifiedCount = 0;
      let communications = [];
      let notifications = [];

      // communications
      responseCommunications = responseCommunications.filter(communication => !communication.read);
      responseCommunications.forEach(communication => {
        let [...existings] = communications.filter(fromLead => fromLead.leadId === communication.leadId);
        if (existings.length === 0) {
          communications.push(communication);
        }
        const [communicationsFocus] = communications
          .filter(fromLead => fromLead.leadId === communication.leadId)
          .map(fromLead => (communication.createdAt > fromLead.createdAt ? communication : fromLead));
        communications = communications.map(fromLead => (communicationsFocus.leadId === fromLead.leadId ? communicationsFocus : fromLead));
      });
      communications = communications
        .map((communication, index) => ({ ...communication, id: `user-${communication.id}`, type: 'client', sender: communication.client, key: index }))
        .sort((notification1, notification2) => {
          return notification2.createdAt > notification1.createdAt ? 1 : -1;
        });
      communications.forEach(communication => {
        if (communication.read === false && !this.unreadMessages.includes(communication.id)) {
          this.unreadMessages.push(communication.id);
        }
      });
      toBeNotifiedCount = communications.reduce((acc, communication) => (communication.notified === false ? ++acc : acc), toBeNotifiedCount);
      unreadCount.communications = communications.reduce((acc, communication) => (communication.read === false ? ++acc : acc), 0);
      remoteNotified.communications = (communications[0] || {}).createdAt || null;

      // notifications
      responseNotifications = responseNotifications.filter(notification => notification.type !== 'lead');
      responseNotifications.forEach(response => {
        const identityFn = notification => notification.carId === response.carId && notification.type === response.type;

        let [...existings] = notifications.filter(identityFn);
        if (existings.length === 0) {
          notifications.push(response);
        } else {
          const [notificationsPicked] = notifications.filter(identityFn).map(notification => (response.createdAt > notification.createdAt ? response : notification));
          notifications = notifications.map(notification => (identityFn(notification) ? notificationsPicked : notification));
        }
      });
      notifications = notifications
        .map((notification, index) => ({
          ...notification,
          id: `sys-${notification.id}`,
          notificationId: notification.id,
          sender: null,
          carId: notification.lead !== undefined ? (notification.lead.carId !== undefined ? notification.lead.carId : null) : (notification.carId),
          key: index
        }))
        .sort((notification1, notification2) => {
          return notification2.createdAt > notification1.createdAt ? 1 : -1;
        });

      // toBeNotifiedCount = notifications.reduce((acc, communication) => (communication.notified === false ? ++acc : acc), toBeNotifiedCount);
      // // potrebbe essere necessario includere la dimensione delle notifiche da sistema
      toBeNotifiedCount += notifications.length;
      unreadCount.notifications = notifications.reduce((acc, communication) => (communication.status === 'created' ? ++acc : acc), 0);
      remoteNotified.notifications = (notifications[0] || {}).createdAt || null;

      // const triggerPopover = toBeNotifiedCount > 0;

      const keyStorage = this.storageKey();
      const storedNotified = JSON.parse(window.localStorage.getItem(keyStorage) || '{}');
      const deltaNotificationsGroup = Object.keys(remoteNotified).reduce((acc, key) => {
        if (
          (storedNotified.hasOwnProperty(key) && (storedNotified[key] < remoteNotified[key] || (storedNotified[key] === null && remoteNotified[key] !== null))) ||
          !storedNotified.hasOwnProperty(key)
        ) {
          return ++acc;
        }
        return acc;
      }, 0);
      const triggerPopover = deltaNotificationsGroup > 0;
      //

      let combined = [].concat(communications).concat(notifications);
      combined = combined
        .sort((notification1, notification2) => {
          return notification2.createdAt > notification1.createdAt ? 1 : -1;
        })
        .map((notification, index) => ({ ...notification, key: index }));

      const unreadCountCombined = Object.keys(unreadCount).reduce((acc, key) => acc + unreadCount[key], 0);

      this.lastList = combined;
      this.lastUnreadCount = unreadCountCombined;
      this.remoteNotified = remoteNotified;

      //
      this.loading = false;
      if (triggerPopover) this.visiblePopover = true;
    });
  }

  @action
  markReadByLeadId(leadId) {
    // contrassegna messaggi letti per lead
    return api('setCommunicationsReadByLead', leadId);
  }

  @action
  markReadByNotificationId(notificationId) {
    // contrassegna notifica letta per id
    return api('setSystemCommunicationsReadById', notificationId).then(response => {
      if ('success' in response && response.success === true) {
        this.lastList = this.lastList.filter(com => com.notificationId !== notificationId);
        this.lastUnreadCount = this.lastList.reduce((acc, curComm) => (curComm.status === 'created' || curComm.read === false ? ++acc : acc), 0);
      }
      return response;
    });
  }

  @action
  markManyReadByNotificationIds(notificationIds) {
    return api('setSystemCommunicationsManyReadByIds', notificationIds).then(response => {
      if ('success' in response && response.success === true) {
        this.lastList = this.lastList.filter(comm => notificationIds.find(id => id === comm.notificationId) === undefined);
        this.lastUnreadCount = this.lastList.reduce((acc, curComm) => (curComm.status === 'created' || curComm.read === false ? ++acc : acc), 0);
      }
      return response;
    });
  }

  @action
  toggleVisiblePopover() {
    this.visiblePopover = !this.visiblePopover;
    if (!this.visiblePopover) {
      // applicare quando le comunicazioni sono da considerare notificate
      api('setCommunicationsNotified').catch(error => {
        console.error(`error in communications server`, error);
      });
    }
  }

  @action
  markAsRead(messagesIds) {
    return new Promise(function(resolve, reject) {
      api('setUnreadMessagesAsRead', messagesIds)
        .then(response => {
          if (!!response.success) {
            resolve(response);
          } else {
            reject();
          }
        })
        .catch(error => {
          console.error(`error in communications server`, error);
        });
    });
  }

  @computed
  get list() {
    return this.lastList;
  }

  @computed
  get unreadCount() {
    // if (this.loading) {
    return this.lastUnreadCount;
    // } else {
    //   return this.list.length;
    // }
  }

  @computed
  get listAsDataSource() {
    return this.list;
  }

  @action
  includedInUnreadThreads(threadId) {
    return this.unreadThreads.includes(threadId);
  }

  @action
  includedInUnreadMessages(messageId) {
    return this.unreadMessages.includes(messageId);
  }

  @action
  storageKey = data => {
    return `io.myair.mydesk.notifications`;
  };

  @action
  storeRemoteNotified = () => {
    const keyStorage = this.storageKey();
    window.localStorage.setItem(keyStorage, JSON.stringify(this.remoteNotified));
  };
}

export default new CommunicationsStore(leadsStore);
